import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle, Bug, Send, CheckCircle2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const ITEMS = [
  {
    id: "decrypt",
    q: "How do I decrypt my journal entries?",
    a: "You don't have to do anything. When you sign in and unlock your vault with your vault passphrase, your browser derives your key and decrypts every entry and attachment locally using AES-256 (GCM). The encrypted data never leaves your device in readable form, and my360boss servers only ever store ciphertext — so decryption always happens automatically, right here in your browser, and nowhere else.",
  },
  {
    id: "email",
    q: "What if my verification or password-reset email hasn't arrived?",
    a: "Give it two or three minutes, then check your spam or promotions folder — our messages come from no-reply@my360boss.com, so adding that address to your contacts helps. If it still hasn't arrived, request the email again from the sign-in screen and double-check the address you entered. Still stuck? Write to founder@my360boss.com and we'll sort it out.",
  },
  {
    id: "recover",
    q: "Can customer support recover my lost vault passphrase?",
    a: "No — and that's by design. my360boss uses a zero-knowledge setup: your vault passphrase never leaves your device, so we cannot read, reset, or recover your entries for you. If you forget your passphrase, use the recovery code you saved when you created the vault. If you have lost both, your existing entries cannot be decrypted by anyone; the only option is to delete the vault and start a fresh one, which permanently wipes the old log for your security. Your regular account password is separate — resetting it does not affect your vault.",
  },
];

export default function VaultHelp() {
  const location = useLocation();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (msg.trim().length < 10) return toast.error("Please describe the problem in at least 10 characters");
    setBusy(true);
    try {
      await api.post("/bug-reports", { message: msg, page: location.pathname });
      setSent(true); setMsg("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send the report");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-12 max-w-3xl mx-auto" data-testid="vault-help">
      <p className="font-mono text-xs uppercase tracking-widest text-emerald-600 mb-2 flex items-center gap-1.5"><HelpCircle size={13} /> Common questions</p>
      <Accordion type="single" collapsible className="bg-white border border-slate-200 rounded-2xl px-5" data-testid="vault-help-accordion">
        {ITEMS.map((it) => (
          <AccordionItem key={it.id} value={it.id} className="border-slate-100">
            <AccordionTrigger data-testid={`vault-help-q-${it.id}`} className="text-left font-semibold text-slate-900 hover:text-emerald-700 hover:no-underline py-4">{it.q}</AccordionTrigger>
            <AccordionContent data-testid={`vault-help-a-${it.id}`} className="text-slate-600 leading-relaxed">{it.a}</AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 mt-5" data-testid="bug-report-box">
        <p className="font-semibold text-slate-900 flex items-center gap-2"><Bug size={16} className="text-emerald-600" /> Report a bug — anonymously</p>
        <p className="text-xs text-slate-500 mt-1">Goes straight to the admin panel. We don't store your name, email, account, or IP with the report — so include steps to reproduce, since we can't follow up.</p>
        {sent ? (
          <p data-testid="bug-report-sent" className="mt-3 text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 size={16} /> Thanks — your report has been sent.</p>
        ) : (
          <form onSubmit={submit} className="mt-3 space-y-2">
            <Textarea data-testid="bug-report-input" value={msg} onChange={(e) => setMsg(e.target.value)} rows={3} placeholder="What happened, and what did you expect? Which page, browser, and device?" className="bg-white text-sm" />
            <Button data-testid="bug-report-submit" type="submit" disabled={busy || msg.trim().length < 10} size="sm" className="rounded-full bg-slate-900 hover:bg-emerald-600 gap-1.5"><Send size={14} /> {busy ? "Sending..." : "Send report"}</Button>
          </form>
        )}
      </div>
    </section>
  );
}
